import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked, ViewChild } from '@angular/core';
import { BaseLayout } from '../base-layout/base-layout';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutService } from '../services/layout.service';
import { Toolbar } from '../components/toolbar/toolbar';
import { Sidenav } from '../components/sidenav/sidenav';
import { VersionBar } from '../components/version-bar/version-bar';
import { Footer } from '../components/footer/footer';
import { Quickpanel } from '../components/quickpanel/quickpanel';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatIcon } from '@angular/material/icon';
import { Navigation } from '../components/navigation/navigation';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { FABRegistryService } from '../services/fab-registry.service';
import { MatButtonModule } from '@angular/material/button';
import { RealtimeService } from '../../notifications/services/realtime.service';
import { NotificationPreferencesService } from '../../notifications/services/notification-preferences.service';
import { CopilotDrawerComponent } from '../../../features/provid/copilot/components/copilot-drawer/copilot-drawer.component';

@Component({
  selector: 'app-layout',
  imports: [
    BaseLayout,
    Toolbar,
    Sidenav,
    VersionBar,
    Navigation,
    Footer,
    Quickpanel,
    RouterOutlet,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    MatIcon,
    MatButtonModule,
    CopilotDrawerComponent
  ],
  templateUrl: './layout.html',
  styleUrl: './layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Layout {
  private router = inject(Router);
  private layoutService = inject(LayoutService);
  private fabRegistry = inject(FABRegistryService);
  private realtimeService = inject(RealtimeService);
  private notificationPrefs = inject(NotificationPreferencesService);

  @ViewChild('quickpanel') quickpanel!: MatSidenav;

  sidenavOpened = this.layoutService.sidenavOpened;
  sidenavMode = this.layoutService.sidenavMode;
  sidenavExpanded = this.layoutService.sidenavExpanded;
  sidenavWidthClass = this.layoutService.sidenavWidthClass;
  isMobile = this.layoutService.isMobile;
  hideVersionBar = this.layoutService.hideVersionBar;

  // Current FAB configuration
  private currentRoute = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    { initialValue: null }
  );

  currentFAB = computed(() => {
    this.currentRoute();
    this.fabRegistry.registrationVersion(); // reage a registros/remoções dinâmicos
    return this.fabRegistry.getFABForRoute(this.router.url);
  });

  // Update layout service with current FAB
  constructor() {
    effect(() => {
      this.layoutService.setFAB(this.currentFAB());
    });

    // Fecha o sidenav mobile automaticamente ao navegar para uma nova rota
    // (clique em item do menu lateral deve levar à rota E fechar o menu).
    // `untracked` evita que a leitura de sidenavOpened()/isMobile() vire
    // dependência do effect — senão ele reexecuta e fecha o menu assim que
    // ele é aberto, impedindo o menu de abrir.
    effect(() => {
      this.currentRoute();
      untracked(() => {
        if (this.isMobile() && this.sidenavOpened()) {
          this.layoutService.closeSidenav();
        }
      });
    });

    // Inicializa conexão WebSocket se notificações estiverem habilitadas
    if (this.notificationPrefs.isEnabled()) {
      this.realtimeService.connect();
    }
  }

  onFABClick(): void {    
    const fab = this.currentFAB();
    if (fab) {
      fab.action();
    }
  }

  closeSidenav(): void {
    if (this.isMobile()) {
      this.layoutService.closeSidenav();
    }
  }

  toggleQuickpanel(): void {
    this.quickpanel?.toggle();
  }

  toggleSidenavExpanded(): void {
    this.layoutService.toggleSidenavExpanded();
  }
}
